import React, { useState, useEffect } from "react";
import IntroAnimation from "./components/IntroAnimation";
import Navbar from "./components/Navbar";
import Home from "./components/Home/Home";
import About from "./components/About/About";
import Projects from "./components/Projects/Projects";
import Footer from "./components/Footer";
import Resume from "./components/Resume/ResumeNew";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation
} from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import "./style.css";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";


function useCustomCursor(active) {
  useEffect(() => {
    if (!active) return;

    // Detect if device has touch capability (mobile/tablet)
    const isTouchDevice = () => {
      return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
    };

    // Don't enable custom cursor on touch devices
    if (isTouchDevice()) {
      return;
    }

    const cursor = document.querySelector('.custom-cursor');
    if (!cursor) return;
    let mouseX = 0;
    let mouseY = 0;
    let cursorX = 0;
    let cursorY = 0;
    let isVisible = true;

    const updateCursor = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      const target = e.target;
      const isInteractive = target.closest && (target.closest('a, button, input, textarea, select, .btn, .nav-link, .project-card, .tech-icons, .home-social-icons, .footer-icons, .resume-btn, .fork-btn, .navbar-toggler, .about-img img, .myAvtar img') ||
        target.matches('a, button, input, textarea, select, .btn, .nav-link, .project-card, .tech-icons, .home-social-icons, .footer-icons, .resume-btn, .fork-btn, .navbar-toggler, svg, img'));
      if (isInteractive) {
        cursor.classList.add('hover');
      } else {
        cursor.classList.remove('hover');
      }
    };

    const handleMouseLeave = () => {
      isVisible = false;
      cursor.style.opacity = '0';
    };
    const handleMouseEnter = () => {
      isVisible = true;
      cursor.style.opacity = '1';
    };
    const animate = () => {
      if (isVisible) {
        const speed = 0.25;
        cursorX += (mouseX - cursorX) * speed;
        cursorY += (mouseY - cursorY) * speed;
        cursor.style.left = `${cursorX}px`;
        cursor.style.top = `${cursorY}px`;
      }
      requestAnimationFrame(animate);
    };
    window.addEventListener('mousemove', updateCursor);
    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseenter', handleMouseEnter);
    animate();
    return () => {
      window.removeEventListener('mousemove', updateCursor);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, [active]);
}

function AppContent() {
  const [showIntro, setShowIntro] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialMount = React.useRef(true);

  // Auto-refresh if user returns after 6 hours of inactivity
  useEffect(() => {
    const REFRESH_THRESHOLD = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
    const LAST_VISIT_KEY = 'portfolio_last_visit';

    const lastVisit = localStorage.getItem(LAST_VISIT_KEY);
    const currentTime = new Date().getTime();

    if (lastVisit) {
      const timeDifference = currentTime - parseInt(lastVisit);

      // If user has been away for more than 6 hours, refresh the page
      if (timeDifference > REFRESH_THRESHOLD) {
        localStorage.setItem(LAST_VISIT_KEY, currentTime.toString());
        window.location.reload();
        return;
      }
    }

    // Update last visit timestamp
    localStorage.setItem(LAST_VISIT_KEY, currentTime.toString());
  }, []);

  useEffect(() => {
    // Only run on initial mount (page load/refresh), not on route changes
    if (isInitialMount.current) {
      isInitialMount.current = false;

      // On page load/refresh, if not on home page, redirect to home
      if (location.pathname !== '/') {
        navigate('/', { replace: true });
      }
      // Clear sessionStorage on page load so intro shows on refresh
      sessionStorage.removeItem('hasNavigated');
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (showIntro) {
      document.body.classList.add('intro-active');
    } else {
      document.body.classList.remove('intro-active');
    }
  }, [showIntro]);

  const handleIntroComplete = () => {
    setShowIntro(false);
  };

  // Only activate custom cursor when intro is not showing
  useCustomCursor(!showIntro);

  return (
    <>
      {showIntro ? (
        <IntroAnimation onComplete={handleIntroComplete} />
      ) : (
        <div className="App" id="scroll">
          <div className="custom-cursor" />
          <Navbar />
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/project" element={<Projects />} />
            <Route path="/about" element={<About />} />
            <Route path="/resume" element={<Resume />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          <Footer />
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
